import { Email } from '@/components/common/text_input/Email';
import { AdminApplicationExportEmailRequestDto } from '@/models/Admin/Emails/EmailRequestDtos';
import { ApplicationTypes } from '@/models/ApplicationType';
import { CountryProps } from '@/models/Country';
import { CountyProps } from '@/models/County';
import { programIdToProgramName, ProgramNames } from '@/models/Program';
import { StateProps } from '@/models/State';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import React from 'react';

type Props = {
  adminApplicationExportForm: AdminApplicationExportEmailRequestDto;
  setAdminApplicationExportForm: React.Dispatch<
    React.SetStateAction<AdminApplicationExportEmailRequestDto>
  >;
  programOptions: ProgramNames[];
  applicationTypeOptions: ApplicationTypes[];
  adminIncludeQuestionResponses: boolean;
  setAdminIncludeQuestionResponses: React.Dispatch<React.SetStateAction<boolean>>;
  adminCountryIdInput: string;
  setAdminCountryIdInput: React.Dispatch<React.SetStateAction<string>>;
  isAdminCountryUS: boolean;
  adminStateId: string;
  setAdminStateId: React.Dispatch<React.SetStateAction<string>>;
  adminCountyId: string;
  setAdminCountyId: React.Dispatch<React.SetStateAction<string>>;
  adminCountyOptions: CountyProps[];
  adminPartnerIdsInput: string;
  setAdminPartnerIdsInput: React.Dispatch<React.SetStateAction<string>>;
  allCountries: CountryProps[];
  allStates: StateProps[];
};

const AdminApplicationExportForm: React.FC<Props> = ({
  adminApplicationExportForm,
  setAdminApplicationExportForm,
  programOptions,
  applicationTypeOptions,
  adminIncludeQuestionResponses,
  setAdminIncludeQuestionResponses,
  adminCountryIdInput,
  setAdminCountryIdInput,
  isAdminCountryUS,
  adminStateId,
  setAdminStateId,
  adminCountyId,
  setAdminCountyId,
  adminCountyOptions,
  adminPartnerIdsInput,
  setAdminPartnerIdsInput,
  allCountries,
  allStates,
}) => {
  return (
    <Stack spacing={2}>
      <Email
        fullWidth
        label='Email'
        required
        value={adminApplicationExportForm.Email ?? ''}
        onChange={(e) =>
          setAdminApplicationExportForm((prev) => ({ ...prev, Email: e.target.value }))
        }
      />
      <FormControl fullWidth>
        <InputLabel id='admin-export-program-label' required>
          Program
        </InputLabel>
        <Select
          labelId='admin-export-program-label'
          value={String(adminApplicationExportForm.Program)}
          label='Program'
          onChange={(e) =>
            setAdminApplicationExportForm((prev) => ({
              ...prev,
              Program: Number(e.target.value),
            }))
          }
        >
          {programOptions.map((programId) => (
            <MenuItem key={programId} value={String(programId)}>
              {programIdToProgramName[programId]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth required>
        <InputLabel id='admin-export-app-type-label'>Application Type</InputLabel>
        <Select
          labelId='admin-export-app-type-label'
          value={String(adminApplicationExportForm.ApplicationType)}
          label='Application Type'
          onChange={(e) =>
            setAdminApplicationExportForm((prev) => ({
              ...prev,
              ApplicationType: Number(e.target.value),
            }))
          }
        >
          {applicationTypeOptions.map((applicationType) => (
            <MenuItem key={applicationType} value={String(applicationType)}>
              {ApplicationTypes[applicationType]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        required
        type='number'
        label='Year'
        value={adminApplicationExportForm.Year}
        onChange={(e) =>
          setAdminApplicationExportForm((prev) => ({
            ...prev,
            Year: Number(e.target.value),
          }))
        }
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={adminIncludeQuestionResponses}
            onChange={(e) => setAdminIncludeQuestionResponses(e.target.checked)}
          />
        }
        label='Include Question Responses'
      />
      <FormControl fullWidth>
        <InputLabel id='admin-export-country-label'>Country</InputLabel>
        <Select
          labelId='admin-export-country-label'
          value={adminCountryIdInput}
          label='Country'
          onChange={(e) => setAdminCountryIdInput(e.target.value)}
        >
          <MenuItem value=''>None</MenuItem>
          {allCountries
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((country) => (
              <MenuItem key={country.id} value={String(country.id)}>
                {country.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      {isAdminCountryUS && (
        <>
          <FormControl fullWidth>
            <InputLabel id='admin-export-state-label'>State</InputLabel>
            <Select
              labelId='admin-export-state-label'
              value={adminStateId}
              label='State'
              onChange={(e) => {
                setAdminStateId(e.target.value);
                setAdminCountyId('');
              }}
            >
              <MenuItem value=''>None</MenuItem>
              {allStates
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {`${state.name} (${state.abbrev})`}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={adminStateId.trim() === ''}>
            <InputLabel id='admin-export-county-label'>County</InputLabel>
            <Select
              labelId='admin-export-county-label'
              value={adminCountyId}
              label='County'
              onChange={(e) => setAdminCountyId(e.target.value)}
            >
              <MenuItem value=''>None</MenuItem>
              {adminCountyOptions.map((county) => (
                <MenuItem key={county.id} value={county.id}>
                  {county.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
      <TextField
        fullWidth
        label='Partner Ids (comma separated)'
        value={adminPartnerIdsInput}
        onChange={(e) => setAdminPartnerIdsInput(e.target.value)}
      />
    </Stack>
  );
};

export default AdminApplicationExportForm;
